import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [showUserForm, setShowUserForm] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStartChat = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.email) {
      setShowUserForm(false);
      setMessages([
        {
          id: 1,
          message: `Hello ${userInfo.name}! Welcome to Astute Builders. How can we help you today?`,
          is_user: false,
          created_at: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      message: inputValue,
      is_user: true,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      await axios.post(`${API}/chat`, {
        session_id: sessionId,
        name: userInfo.name,
        email: userInfo.email,
        message: inputValue,
        is_user: true,
      });

      // Fetch updated messages
      const response = await axios.get(`${API}/chat/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          message: "Thank you for reaching out! Our team will respond shortly. For immediate assistance, call 862-350-7122.",
          is_user: false,
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-widget" data-testid="live-chat">
      {/* Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="chat-bubble"
          data-testid="chat-bubble"
        >
          <MessageCircle size={28} className="text-[#0A0A0A]" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window" data-testid="chat-window">
          {/* Header */}
          <div className="bg-[#7ED4C6] p-4 flex items-center justify-between">
            <div>
              <h3 className="font-['Oswald'] text-lg font-bold text-[#0A0A0A]">
                Live Chat
              </h3>
              <p className="text-xs text-[#0A0A0A]/70">
                We typically reply within minutes
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-[#0A0A0A] hover:text-[#0A0A0A]/70 transition-colors"
              data-testid="close-chat"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Form or Messages */}
          {showUserForm ? (
            <form onSubmit={handleStartChat} className="p-4 space-y-4">
              <p className="text-sm text-[#A1A1AA]">
                Please provide your details to start the chat.
              </p>
              <input
                type="text"
                placeholder="Your Name"
                value={userInfo.name}
                onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                className="form-input w-full"
                required
                data-testid="chat-name-input"
              />
              <input
                type="email"
                placeholder="Your Email"
                value={userInfo.email}
                onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                className="form-input w-full"
                required
                data-testid="chat-email-input"
              />
              <button type="submit" className="btn-primary w-full" data-testid="start-chat-btn">
                Start Chat
              </button>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="h-72 overflow-y-auto p-4 space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.is_user ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 text-sm ${
                        msg.is_user
                          ? "bg-[#7ED4C6] text-[#0A0A0A]"
                          : "bg-[#1E1E1E] text-white"
                      }`}
                    >
                      {msg.message}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-[#1E1E1E] p-3">
                      <Loader2 size={18} className="animate-spin text-[#7ED4C6]" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-[#2A2A2A]">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="form-input flex-1"
                    data-testid="chat-message-input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-[#7ED4C6] p-3 hover:bg-[#D4B03A] transition-colors disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    <Send size={18} className="text-[#0A0A0A]" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};
